'use client'

import React, {useCallback, useEffect, useRef, useState} from 'react'
import {flushSync} from 'react-dom'
import {cn} from '../../../lib/utils'
import {Button} from '@fitro/customer-ui/Button'
import {Input, Textarea} from '@fitro/customer-ui/Input'
import {consumeTimedClickSuppression, setTimedClickSuppression, suppressNextCompatibleClick} from '../../../lib/pointer-events'
import {useTranslation} from '../../../hooks/useTranslation'

type FieldType = 'text' | 'phone' | 'email' | 'date' | 'textarea'

const PEN_TAP_MOVE_TOLERANCE_PX = 8

type PenDisplayTap = {
    pointerId: number
    clientX: number
    clientY: number
    canceled: boolean
}

interface ModalFieldProps {
    label: string

    value: string

    type?: FieldType

    editable?: boolean

    required?: boolean

    placeholder?: string

    error?: string

    className?: string

    onChange?: (value: string) => void

    onEditToggle?: (isEditing: boolean) => void

    onSave?: (value: string) => void

    onCancel?: () => void

    loading?: boolean

    customActions?: React.ReactNode

    showActions?: boolean

    maxLength?: number

    rows?: number
}

const PhoneIcon = ({className}: { className?: string }) => (
    <svg className={cn('w-4 h-4', className)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21L6.16 10.928c-.5.28-.709.857-.528 1.396a14.028 14.028 0 006.017 6.017c.54.181 1.116-.028 1.396-.528l1.541-4.064a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.948V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
    </svg>
)

const EmailIcon = ({className}: { className?: string }) => (
    <svg className={cn('w-4 h-4', className)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 7.2a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
    </svg>
)

const CopyIcon = ({className}: { className?: string }) => (
    <svg className={cn('w-4 h-4', className)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
    </svg>
)

const EditIcon = ({className}: { className?: string }) => (
    <svg className={cn('w-4 h-4', className)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
    </svg>
)

const CheckIcon = ({className}: { className?: string }) => (
    <svg className={cn('w-4 h-4', className)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/>
    </svg>
)

const XIcon = ({className}: { className?: string }) => (
    <svg className={cn('w-4 h-4', className)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
    </svg>
)

export const ModalField: React.FC<ModalFieldProps> = ({
                                                          label,
                                                          value,
                                                          type = 'text',
                                                          editable = true,
                                                          required = false,
                                                          placeholder,
                                                          error,
                                                          className,
                                                          onChange,
                                                          onEditToggle,
                                                          onSave,
                                                          onCancel,
                                                          loading = false,
                                                          customActions,
                                                          showActions = true,
                                                          maxLength,
                                                          rows = 3
}) => {
    const {dateTime} = useTranslation()
    const [isEditing, setIsEditing] = useState(false)
    const [editValue, setEditValue] = useState(value)
    const [copySuccess, setCopySuccess] = useState(false)
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null)
    const penDisplayTapRef = useRef<PenDisplayTap | null>(null)
    const suppressNextEditClickRef = useRef<boolean | null>(null)
    const suppressEditClickTimeoutRef = useRef<number | null>(null)
    const fieldInputId = React.useId()

    useEffect(() => {
        if (!isEditing) {
            setEditValue(value)
        }
    }, [value, isEditing])

    const focusEditingInput = useCallback(() => {
        if (!inputRef.current) return

        inputRef.current.focus()
        if (inputRef.current instanceof HTMLInputElement) {
            inputRef.current.select()
        }
    }, [])

    useEffect(() => {
        if (isEditing) {
            focusEditingInput()
        }
    }, [focusEditingInput, isEditing])

    useEffect(() => {
        const clearPenTap = () => {
            penDisplayTapRef.current = null
        }

        document.addEventListener('scroll', clearPenTap, true)
        document.addEventListener('lostpointercapture', clearPenTap, true)

        return () => {
            document.removeEventListener('scroll', clearPenTap, true)
            document.removeEventListener('lostpointercapture', clearPenTap, true)
        }
    }, [])

    const openEditingWithImmediateFocus = () => {
        if (isEditing) return

        flushSync(() => {
            setIsEditing(true)
            onEditToggle?.(true)
        })

        focusEditingInput()
    }

    const handleEditToggle = () => {
        const newEditingState = !isEditing
        setIsEditing(newEditingState)
        onEditToggle?.(newEditingState)

        if (!newEditingState) {
            setEditValue(value)
        }
    }

    const handleDisplayClick = () => {
        if (consumeTimedClickSuppression(suppressNextEditClickRef, true, suppressEditClickTimeoutRef)) return

        handleEditToggle()
    }

    const handleDisplayPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
        if (!editable || event.pointerType !== 'pen' || event.isPrimary === false || event.button !== 0) {
            penDisplayTapRef.current = null
            return
        }

        penDisplayTapRef.current = {
            pointerId: event.pointerId,
            clientX: event.clientX,
            clientY: event.clientY,
            canceled: false,
        }
    }

    const clearDisplayPointerTap = () => {
        penDisplayTapRef.current = null
    }

    const handleDisplayPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
        const penTap = penDisplayTapRef.current
        if (!penTap || event.pointerType !== 'pen' || event.isPrimary === false || penTap.pointerId !== event.pointerId) {
            return
        }

        if (Math.hypot(event.clientX - penTap.clientX, event.clientY - penTap.clientY) > PEN_TAP_MOVE_TOLERANCE_PX) {
            penTap.canceled = true
        }

        queueMicrotask(() => {
            if (penDisplayTapRef.current === penTap && event.defaultPrevented) {
                penTap.canceled = true
            }
        })
    }

    const handleDisplayPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
        const penTap = penDisplayTapRef.current
        penDisplayTapRef.current = null
        if (!editable || event.pointerType !== 'pen' || event.isPrimary === false || !penTap) return
        if (penTap.pointerId !== event.pointerId) return
        if (penTap.canceled) return
        if (Math.hypot(event.clientX - penTap.clientX, event.clientY - penTap.clientY) > PEN_TAP_MOVE_TOLERANCE_PX) return

        event.preventDefault()
        event.stopPropagation()
        suppressNextCompatibleClick(event)
        setTimedClickSuppression(suppressNextEditClickRef, true, suppressEditClickTimeoutRef)
        openEditingWithImmediateFocus()
    }

    const handleSave = () => {
        if (editValue.trim() === '') {
            if (required) {
                return
            }
        }

        onSave?.(editValue)
        setIsEditing(false)
        onEditToggle?.(false)
    }

    const handleCancel = () => {
        setEditValue(value)
        setIsEditing(false)
        onEditToggle?.(false)
        onCancel?.()
    }

    const handleInputChange = (newValue: string) => {
        setEditValue(newValue)
        onChange?.(newValue)
    }

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' && type !== 'textarea') {
            event.preventDefault()
            handleSave()
        } else if (event.key === 'Escape') {
            event.preventDefault()
            handleCancel()
        }
    }

    const handlePhoneCall = () => {
        if (value && type === 'phone') {
            window.open(`tel:${value}`, '_self')
        }
    }

    const handleEmailSend = () => {
        if (value && type === 'email') {
            window.open(`mailto:${value}`, '_self')
        }
    }

    const handleCopyToClipboard = async () => {
        if (!value) return

        try {
            await navigator.clipboard.writeText(value)
            setCopySuccess(true)
            setTimeout(() => setCopySuccess(false), 2000)
        } catch {
            const textarea = document.createElement('textarea')
            textarea.value = value
            document.body.appendChild(textarea)
            textarea.select()
            document.execCommand('copy')
            document.body.removeChild(textarea)
            setCopySuccess(true)
            setTimeout(() => setCopySuccess(false), 2000)
        }
    }

    const formatDisplayValue = (val: string) => {
        if (!val) return '—'

        switch (type) {
            case 'phone':
                const cleaned = val.replace(/\D/g, '')
                if (cleaned.startsWith('48') && cleaned.length === 11) {
                    return `+48 ${cleaned.slice(2, 5)} ${cleaned.slice(5, 8)} ${cleaned.slice(8)}`
                }
                return val

            case 'email':
                return val.toLowerCase()

            case 'date':
                return dateTime.formatLocalDate(val)

            default:
                return val
        }
    }

    const getInputType = () => {
        switch (type) {
            case 'email':
                return 'email'
            case 'phone':
                return 'tel'
            case 'date':
                return 'date'
            default:
                return 'text'
        }
    }

    const getFieldActions = () => {
        if (!showActions) return []
        if (customActions) return React.isValidElement(customActions) ? [customActions] : customActions
        if (!value) return []

        const actions = []

        if (type === 'phone') {
            actions.push(
                <Button
                    key="call"
                    type="button"
                    variant="ghost"
                    size="small"
                    iconOnly
                    onClick={handlePhoneCall}
                    className="text-green-600 hover:text-green-700 hover:bg-green-50 dark:text-green-400 dark:hover:text-green-300 dark:hover:bg-green-900/20"
                    title="Call this number"
                    aria-label={`Call ${value}`}
                >
                    <PhoneIcon/>
                </Button>
            )
        }

        if (type === 'email') {
            actions.push(
                <Button
                    key="email"
                    type="button"
                    variant="ghost"
                    size="small"
                    iconOnly
                    onClick={handleEmailSend}
                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:text-blue-400 dark:hover:text-blue-300 dark:hover:bg-blue-900/20"
                    title="Send email"
                    aria-label={`Send email to ${value}`}
                >
                    <EmailIcon/>
                </Button>
            )
        }

        actions.push(
            <Button
                key="copy"
                type="button"
                variant="ghost"
                size="small"
                iconOnly
                onClick={handleCopyToClipboard}
                className={cn(
                    "transition-colors duration-200",
                    copySuccess
                        ? "text-green-600 dark:text-green-400"
                        : "text-gray-500 hover:text-gray-700 hover:bg-gray-50 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-700"
                )}
                title={copySuccess ? "Copied!" : "Copy to clipboard"}
                aria-label={`Copy ${label} to clipboard`}
            >
                {copySuccess ? <CheckIcon/> : <CopyIcon/>}
            </Button>
        )

        return actions
    }

    const fieldActions = getFieldActions()

    return (
        <div className={cn('group relative', className)}>
            <div className="flex items-start justify-between gap-3 min-h-[44px]">
                <div className="flex-1 min-w-0">

                    <label
                        htmlFor={isEditing ? fieldInputId : undefined}
                        className="block text-body-small font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >
                        {label}
                        {required && (
                            <span className="ml-1 text-error-500" aria-label="required">
                *
              </span>
                        )}
                    </label>


                    {isEditing ? (
                        <div className="space-y-2">
                            {type === 'textarea' ? (
                                <Textarea
                                    id={fieldInputId}
                                    ref={inputRef as React.RefObject<HTMLTextAreaElement>}
                                    value={editValue}
                                    onChange={(e) => handleInputChange(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder={placeholder}
                                    rows={rows}
                                    maxLength={maxLength}
                                    variant={error ? 'error' : 'default'}
                                    errorText={error}
                                    className="text-body"
                                    disabled={loading}
                                />
                            ) : (
                                <Input
                                    id={fieldInputId}
                                    ref={inputRef as React.RefObject<HTMLInputElement>}
                                    type={getInputType()}
                                    value={editValue}
                                    onChange={(e) => handleInputChange(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder={placeholder}
                                    maxLength={maxLength}
                                    variant={error ? 'error' : 'default'}
                                    errorText={error}
                                    className="text-body"
                                    disabled={loading}
                                />
                            )}


                            <div className="flex items-center gap-2">
                                <Button
                                    type="button"
                                    variant="primary"
                                    size="small"
                                    onClick={handleSave}
                                    disabled={loading || (required && !editValue.trim())}
                                    loading={loading}
                                    leftIcon={<CheckIcon/>}
                                    className="min-h-[36px]"
                                >
                                    Save
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="small"
                                    onClick={handleCancel}
                                    disabled={loading}
                                    leftIcon={<XIcon/>}
                                    className="min-h-[36px]"
                                >
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div
                            className={cn(
                                'flex items-center gap-2 min-h-[32px] group cursor-pointer rounded-md',
                                'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900'
                            )}
                            onClick={editable ? handleDisplayClick : undefined}
                            onPointerDown={handleDisplayPointerDown}
                            onPointerMove={handleDisplayPointerMove}
                            onPointerUp={handleDisplayPointerUp}
                            onPointerCancel={clearDisplayPointerTap}
                            onPointerLeave={clearDisplayPointerTap}
                            role={editable ? "button" : undefined}
                            tabIndex={editable ? 0 : undefined}
                            onKeyDown={(e) => {
                                if (editable && (e.key === 'Enter' || e.key === ' ')) {
                                    e.preventDefault()
                                    handleEditToggle()
                                }
                            }}
                            aria-label={editable ? `Edit ${label}` : undefined}
                        >
              <span
                  className={cn(
                      'text-body flex-1 min-w-0 break-words',
                      value
                          ? 'text-gray-900 dark:text-gray-100'
                          : 'text-gray-500 dark:text-gray-400 italic',
                      editable && 'group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-200'
                  )}
              >
                {value ? formatDisplayValue(value) : (placeholder || 'Not set')}
              </span>

                            {editable && (
                                <EditIcon className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity duration-200 shrink-0"/>
                            )}
                        </div>
                    )}
                </div>


                {!isEditing && fieldActions && Array.isArray(fieldActions) && fieldActions.length > 0 && (
                    <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-200">
                        {fieldActions.map((action, index) => (
                            <React.Fragment key={index}>
                                {action}
                            </React.Fragment>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}

ModalField.displayName = 'ModalField'
